/**
 * `PersonNameLink` — единый паттерн «ссылка на человека» (задача 78 §2.2).
 *
 * Имя сотрудника во всех списках/карточках ведёт на его карточку `/people/$id`
 * (`PeopleDetailPage`), откуда можно внести отпуск / отгул / больничный. Единый
 * визуальный вариант: `hover:underline`, наследует цвет/начертание контекста.
 *
 * Без `id` (нет `user_id`/`person_id`) — отдаём текст в `<span>`, чтобы не плодить
 * битые ссылки. Роль-гейт `/people/$id` живёт на backend (`usePerson`) — фронт
 * авторитет не дублирует. Владелец паттерна; 82 §1.2 переиспользует его.
 *
 * Навигация к карточке человека может быть выключена окружением через
 * `PeopleNavContext` (задача 82 §1.3) — на мобильной поверхности учёта времени
 * `/people/$id` не адаптирована, поэтому имя там рендерится той же ветвью-`<span>`,
 * что и при отсутствии `id`. Дефолт контекста — `enabled=true` (ссылка).
 */
import { Link } from "@tanstack/react-router";

import { usePeopleNavEnabled } from "@/components/people/PeopleNavContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface PersonNameLinkProps {
  id?: number | null;
  name: string;
  fullName?: string;
  className?: string;
}

export function PersonNameLink({
  id,
  name,
  fullName,
  className,
}: PersonNameLinkProps): React.ReactElement {
  const navEnabled = usePeopleNavEnabled();
  const content =
    id == null || !navEnabled ? (
      <span className={className}>{name}</span>
    ) : (
      <Link
        to="/people/$id"
        params={{ id: String(id) }}
        className={className}
        data-testid={`person-name-link-${id}`}
      >
        {name}
      </Link>
    );
  if (!fullName) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{fullName}</TooltipContent>
    </Tooltip>
  );
}
